import { useLazyQuery, useMutation } from '@apollo/client';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { getActiveHouseId, identifyMe } from '~/components/_app/initializeSession';
import { formatPhoneE164 } from '~/utils/formatPhoneNumber';
import PageLoading from '../components/PageLoading';
import { GET_ME, LOGIN_CUSTOMER_WITH_MAGIC_LINK } from '../queries/initialize';
import { useActiveHouseId } from './useActiveHouseId';
import useEventTracker from './useEventTracker';
import { useIsInitializing } from './useIsInitializing';
import { useMe } from './useMe';
import type { me as Me, me_me_Customer, me_me_Lead } from '../types/generated/me';
import type {
  loginCustomerWithMagicLink,
  loginCustomerWithMagicLinkVariables,
} from '../types/generated/loginCustomerWithMagicLink';
import type { ReactNode } from 'react';

/**
 * @deprecated this is now handled in src/auth/magicLink.ts
 */
function useMagicLink(waiting: boolean): { isLoggingIn: boolean } {
  const { setMe } = useMe<me_me_Lead | me_me_Customer>();
  const router = useRouter();
  const query = router.query as { magicLink: string };
  const { setActiveHouseId } = useActiveHouseId();
  const [loginSuccess, setLoginSuccess] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(true);
  const trackEvent = useEventTracker();

  const magicLink = query.magicLink;
  const [loginCustomerWithMagicLink] = useMutation<
    loginCustomerWithMagicLink,
    loginCustomerWithMagicLinkVariables
  >(LOGIN_CUSTOMER_WITH_MAGIC_LINK, {
    variables: { token: magicLink },
    onCompleted: async data => {
      setIsLoggingIn(false);
      if (data.loginCustomerWithMagicLink.__typename === 'Customer') {
        const customer = data.loginCustomerWithMagicLink;
        setLoginSuccess(true);
        void trackEvent('login', {
          method: 'magic_link',
          user_id: customer.id,
          user_type: customer.__typename,
          email: customer.email,
          name: `${customer.firstName} ${customer.lastName}`,
          phone: formatPhoneE164(customer.phone),
          intercom_hash: customer.intercomHash,
        });
      }
    },
    onError: () => setIsLoggingIn(false),
  });

  const [getMe, { data: meData }] = useLazyQuery<Me>(GET_ME, {
    fetchPolicy: 'network-only',
    onCompleted: ({ me }) => {
      if (me.__typename === 'Customer') {
        const myHouseId = getActiveHouseId(me);
        identifyMe(me);
        setMe(me);
        setActiveHouseId(myHouseId);
      }
    },
  });

  useEffect(() => {
    if (!meData && loginSuccess) void getMe();
  }, [getMe, loginSuccess, meData]);

  useEffect(() => {
    if (magicLink && !waiting) {
      setIsLoggingIn(true);
      void loginCustomerWithMagicLink();
    } else if (router.isReady && !magicLink) {
      setIsLoggingIn(false);
    }
  }, [magicLink, loginCustomerWithMagicLink, waiting, router.isReady]);

  return { isLoggingIn };
}

export const MagicLinkProvider = ({ children }: { children: ReactNode }) => {
  const { isInitializing } = useIsInitializing();
  const { isLoggingIn } = useMagicLink(isInitializing);
  return isLoggingIn ? <PageLoading /> : <>{children}</>;
};
